import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["input", "button"];
  static values = { message: String };
  static classes = ["error"];

  static MONTHS = [
    "1月",
    "2月",
    "3月",
    "4月",
    "5月",
    "6月",
    "7月",
    "8月",
    "9月",
    "10月",
    "11月",
    "12月",
  ];
  static ERROR_MESSAGE = "この項目は必須です。";

  connect() {
    this.boundHandleClickOutside = this.handleClickOutside.bind(this);
    this.setupValidationSync();
  }

  setupValidationSync() {
    this.inputTarget.addEventListener("invalid", (e) => {
      e.preventDefault();
      this.showError();
    });

    this.inputTarget.addEventListener("change", () => {
      if (this.inputTarget.value) {
        this.clearError();
      }
    });
  }

  showError() {
    if (this.hasErrorClass) {
      this.inputTarget.classList.add(this.errorClass);
    } else {
      this.inputTarget.classList.add("is-error");
    }

    const errorMessageBlock = this.element.querySelector(".error-message");
    if (errorMessageBlock) {
      errorMessageBlock.textContent = this.constructor.ERROR_MESSAGE;
    }
  }

  clearError() {
    if (this.hasErrorClass) {
      this.inputTarget.classList.remove(this.errorClass);
    } else {
      this.inputTarget.classList.remove("is-error");
    }

    const errorMessageBlock = this.element.querySelector(".error-message");
    if (errorMessageBlock) {
      errorMessageBlock.textContent = "";
    }
  }

  disconnect() {
    this.closeCalendar();
  }

  openCalendar(event) {
    event.preventDefault();

    this.closeCalendar();

    this.calendar = this.createCalendar();
    document.body.appendChild(this.calendar);

    this.positionCalendar();

    setTimeout(() => {
      document.addEventListener("click", this.boundHandleClickOutside);
    }, 0);
  }

  closeCalendar() {
    if (this.calendar) {
      this.calendar.remove();
      this.calendar = null;
      document.removeEventListener("click", this.boundHandleClickOutside);
    }
  }

  handleClickOutside(event) {
    if (
      this.calendar &&
      !this.calendar.contains(event.target) &&
      !this.buttonTarget.contains(event.target)
    ) {
      this.closeCalendar();
    }
  }

  createCalendar() {
    const container = document.createElement("div");
    container.className = "month-picker-container";


    const currentValue = this.inputTarget.value;
    let currentYear, currentMonth;

    if (currentValue) {
      // YYYY年M月形式
      let match = currentValue.match(/(\d{4})年(\d{1,2})月/);
      if (match) {
        currentYear = parseInt(match[1]);
        currentMonth = parseInt(match[2]) - 1;
      } else {
        // YYYY/MM形式
        match = currentValue.match(/(\d{4})\/(\d{1,2})/);
        if (match) {
          currentYear = parseInt(match[1]);
          currentMonth = parseInt(match[2]) - 1;
        }
      }
    }

    if (typeof currentYear === "undefined" || typeof currentMonth === "undefined") {
      const now = new Date();
      currentYear = now.getFullYear();
      currentMonth = now.getMonth();
    }

    this.currentYear = currentYear;
    this.selectedYear = currentYear;
    this.selectedMonth = currentMonth;

    const header = this.createHeader();
    container.appendChild(header);

    this.monthsGrid = this.createMonthsGrid();
    container.appendChild(this.monthsGrid);

    const buttonGroup = this.createButtonGroup();
    container.appendChild(buttonGroup);

    if (this.messageValue) {
      const message = this.createMessage();
      container.appendChild(message);
    }

    return container;
  }

  createHeader() {
    const header = document.createElement("div");
    header.className = "month-picker-header";

    const prevButton = document.createElement("button");
    prevButton.type = "button";
    prevButton.className = "month-picker-nav-button is-prev";
    prevButton.innerHTML = "";
    prevButton.addEventListener("click", (e) => {
      e.preventDefault();
      this.currentYear--;
      this.updateYearDisplay();
      this.updateMonthsGrid();
    });

    this.yearDisplay = document.createElement("div");
    this.yearDisplay.className = "month-picker-year";
    this.yearDisplay.textContent = `${this.currentYear}年`;

    const nextButton = document.createElement("button");
    nextButton.type = "button";
    nextButton.className = "month-picker-nav-button is-next";
    nextButton.innerHTML = "";
    nextButton.addEventListener("click", (e) => {
      e.preventDefault();
      this.currentYear++;
      this.updateYearDisplay();
      this.updateMonthsGrid();
    });

    header.appendChild(prevButton);
    header.appendChild(this.yearDisplay);
    header.appendChild(nextButton);

    return header;
  }

  createMonthsGrid() {
    const grid = document.createElement("div");
    grid.className = "month-picker-months";

    this.constructor.MONTHS.forEach((monthName, index) => {
      const monthButton = document.createElement("button");
      monthButton.type = "button";
      monthButton.className = "month-picker-month";
      monthButton.textContent = monthName;
      monthButton.dataset.month = index;

      if (
        this.currentYear === this.selectedYear &&
        index === this.selectedMonth
      ) {
        monthButton.classList.add("is-selected");
      }

      monthButton.addEventListener("click", (e) => {
        e.preventDefault();
        this.selectMonth(index);
      });

      grid.appendChild(monthButton);
    });

    return grid;
  }

  createMessage() {
    const message = document.createElement("p");
    message.className = "month-picker-message";
    message.textContent = this.messageValue;
    return message;
  }

  createButton(className, text, handler) {
    const button = document.createElement("button");
    button.type = "button";
    button.className = className;
    button.textContent = text;
    button.addEventListener("click", (e) => {
      e.preventDefault();
      handler();
    });
    return button;
  }

  createButtonGroup() {
    const buttonGroup = document.createElement("div");
    buttonGroup.className = "month-picker-button-group";

    const clearButton = this.createButton("month-picker-clear", "クリア", () =>
      this.clearSelection()
    );

    const confirmButton = this.createButton(
      "month-picker-confirm",
      "決定する",
      () => this.confirmSelection()
    );

    buttonGroup.appendChild(clearButton);
    buttonGroup.appendChild(confirmButton);

    return buttonGroup;
  }

  selectMonth(monthIndex) {
    this.selectedYear = this.currentYear;
    this.selectedMonth = monthIndex;
    this.updateMonthsGrid();
  }

  updateYearDisplay() {
    this.yearDisplay.textContent = `${this.currentYear}年`;
  }

  updateMonthsGrid() {
    const monthButtons = this.monthsGrid.querySelectorAll(
      ".month-picker-month"
    );
    monthButtons.forEach((button, index) => {
      if (
        this.currentYear === this.selectedYear &&
        index === this.selectedMonth
      ) {
        button.classList.add("is-selected");
      } else {
        button.classList.remove("is-selected");
      }
    });
  }

  confirmSelection() {
    this.inputTarget.value = `${this.selectedYear}/${String(this.selectedMonth + 1).padStart(2, "0")}`;

    this.inputTarget.dispatchEvent(
      new Event("change", { bubbles: true })
    );
    this.triggerValidation();
    this.closeCalendar();
  }

  clearSelection() {
    this.inputTarget.value = "";

    this.triggerValidation();
    if (this.inputTarget.hasAttribute("required")) {
      this.showError();
    }

    this.closeCalendar();
  }

  triggerValidation() {
    // フォームのvalidationsコントローラーを取得してtoggleSubmitButtonを呼び出す
    const form = this.element.closest("form");
    if (form) {
      const validationsController =
        this.application.getControllerForElementAndIdentifier(
          form,
          "forms--validations"
        );
      if (validationsController) {
        validationsController.toggleSubmitButton();
      }
    }
  }

  positionCalendar() {
    const buttonRect = this.buttonTarget.getBoundingClientRect();
    const calendarRect = this.calendar.getBoundingClientRect();

    // ボタンの下に配置
    let top = buttonRect.bottom + window.scrollY + 8;
    let left = buttonRect.left + window.scrollX;

    // 画面右端に収まるように調整（document座標で比較）
    const rightEdge = window.scrollX + window.innerWidth;
    if (left + calendarRect.width > rightEdge) {
      left = rightEdge - calendarRect.width - 16;
    }

    // 画面左端に収まるように調整（左側はみ出し防止）
    if (left < window.scrollX + 16) {
      left = window.scrollX + 16;
    }

    // 画面下端に収まるように調整
    if (top + calendarRect.height > window.innerHeight + window.scrollY) {
      top = buttonRect.top + window.scrollY - calendarRect.height - 8;
    }

    this.calendar.style.position = "absolute";
    this.calendar.style.top = `${top}px`;
    this.calendar.style.left = `${left}px`;
  }
}
